/**
 * 처방 판정 테스트 패널 본문(설계 결정 8·9·11·12·13).
 *
 * 관리자 목록 화면에서 우측 슬라이드로 열려, 체중·월령·약품 행을 입력하고 판정 API를 호출해
 * 결과를 등급 배지·사유·룰 링크로 보여준다. 판정은 읽기 전용(DB 쓰기 0). 캐시 없음.
 * 입력·요청 변환·판정 해석은 순수 함수(formUtils·verdict·cardModel)에 위임하고 여기선 조립·상태만.
 */
import React, { useState } from "react";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import DrugSearchInput from "components/molecules/DrugSearchInput";
import { ConfirmManager } from "components/organisms/CommonUI";
import { requestDoseReview, DoseReviewResult } from "api/doseReviewService";
import {
  FormState,
  RowState,
  ROW_COLUMN_LABELS,
  emptyForm,
  emptyRow,
  canRunReview,
  toRequest,
  formSignature,
  rowNeedsCodeWarning,
  setAgeMonths,
  setAgeYears,
  rowFromDrug,
  rowWithName,
} from "./formUtils";
import { Preset, defaultPresets, PRESET_GENERATED_AT } from "./presets";
import ResultView from "components/organisms/DoseReviewResultView";
import RawView from "components/organisms/DoseReviewResultView/RawView";
import {
  Card,
  InfoLine,
  Row,
  WarnText,
} from "components/organisms/DoseReviewResultView/style";
import {
  Panel,
  PanelTitle,
  Section,
  SectionTitle,
  Field,
  NumberInput,
  PresetNote,
  PresetSelect,
  Table,
  HeaderRow,
  DrugRow,
  CellInput,
  CodeText,
  ResultSection,
  ResultHeader,
  ChangedBanner,
  EmptyText,
} from "./style";

interface Props {
  presets?: Preset[];
  /** 룰 id 클릭 — 패널을 닫고 룰 상세로 이동(설계 결정 12). 페이지가 주입한다. */
  onOpenRule: (ruleId: string) => void;
}

// 슬라이드를 닫는 애니메이션 도중 다시 열려도 새 행 상태가 되도록 전역 증가 카운터로 key를 만든다.
let rowSeq = 0;
const nextRowId = (): string => `dose-row-${++rowSeq}`;

/** 입력이 하나라도 있는가(프리셋 덮어쓰기 확인 여부, 설계 결정 13 ⑦). */
const formHasInput = (f: FormState): boolean => {
  if (f.weight.trim() || f.ageMonths.trim() || f.ageYears.trim()) return true;
  if (f.rows.length !== 1) return true;
  return f.rows.some(
    (r) =>
      r.medicationName.trim() ||
      r.standardCode.trim() ||
      r.insuranceCode.trim() ||
      r.itemCode.trim() ||
      r.dose.trim() ||
      r.freq.trim()
  );
};

/** 프리셋 → 폼(AC-65). rows[].itemCode로 검색 칸의 "선택됨" 상태를 맞춘다. */
const presetToForm = (preset: Preset): FormState => {
  const patient = preset.request.patient ?? {};
  return {
    weight: patient.weightKg != null ? String(patient.weightKg) : "",
    ageMonths: patient.ageMonths != null ? String(patient.ageMonths) : "",
    ageYears: "",
    rows: preset.request.prescriptions.map((p, i) => ({
      id: nextRowId(),
      medicationName: p.medicationName ?? "",
      standardCode: p.standardCode ?? "",
      insuranceCode: p.insuranceCode ?? "",
      itemCode: preset.rows[i]?.itemCode ?? "",
      dose: p.dosagePerAdministration != null ? String(p.dosagePerAdministration) : "",
      freq: p.timesPerDay != null ? String(p.timesPerDay) : "",
    })),
  };
};

/** 오류 카드 — 원인 4갈래별 문구(설계 결정 8, AC-54~56). */
const ErrorCard: React.FC<{ result: DoseReviewResult }> = ({ result }) => {
  const lines: string[] = [];
  if (result.status === 403) {
    lines.push("관리자 권한으로 판정 API에 접근할 수 없습니다 — 백엔드 배포를 확인하세요");
  } else if (result.status === null) {
    lines.push("응답 없음 — 네트워크 또는 시간 초과(10초)");
  } else if (result.status === 400) {
    const m = (result.body as { message?: unknown })?.message;
    if (Array.isArray(m)) lines.push(...m.map(String));
    else lines.push(result.message ?? "요청이 올바르지 않습니다");
  } else {
    lines.push(`요청 실패 (HTTP ${result.status ?? "?"})${result.message ? ` — ${result.message}` : ""}`);
  }
  return (
    <Card data-testid="dose-error">
      {lines.map((l, i) => (
        <InfoLine key={i}>{l}</InfoLine>
      ))}
    </Card>
  );
};

const DoseReviewTestPanel: React.FC<Props> = ({ presets = defaultPresets, onOpenRule }) => {
  const [form, setForm] = useState<FormState>(() => emptyForm(nextRowId()));
  const [result, setResult] = useState<DoseReviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [lastRunSignature, setLastRunSignature] = useState<string | null>(null);
  const [presetId, setPresetId] = useState("");

  const updateRow = (id: string, updater: (r: RowState) => RowState) =>
    setForm((f) => ({ ...f, rows: f.rows.map((r) => (r.id === id ? updater(r) : r)) }));

  const addRow = () => setForm((f) => ({ ...f, rows: [...f.rows, emptyRow(nextRowId())] }));
  const removeRow = (id: string) =>
    setForm((f) => ({ ...f, rows: f.rows.filter((r) => r.id !== id) }));

  const applyPreset = (preset: Preset) => {
    setForm(presetToForm(preset));
    setPresetId(preset.id);
  };

  const onPresetChange = (id: string) => {
    const preset = presets.find((p) => p.id === id);
    if (!preset) return;
    if (formHasInput(form)) {
      ConfirmManager.show({
        title: "입력 덮어쓰기",
        body: "입력을 프리셋으로 덮어쓸까요?",
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            type="Text"
            size="Medium"
            variant="Ghost"
            color="Secondary"
            onClick={() => ConfirmManager.hide()}
          >
            취소
          </Button>,
          <Button
            key="ok"
            buttonType="button"
            type="Text"
            size="Medium"
            variant="Contained"
            color="Primary"
            onClick={() => {
              ConfirmManager.hide();
              applyPreset(preset);
            }}
          >
            덮어쓰기
          </Button>,
        ],
      });
    } else {
      applyPreset(preset);
    }
  };

  const onRun = async () => {
    if (!canRunReview(form) || loading) return;
    const req = toRequest(form);
    const sig = formSignature(form);
    setLoading(true);
    const res = await requestDoseReview(req);
    setResult(res);
    setLastRunSignature(sig);
    setLoading(false);
  };

  const runnable = canRunReview(form);
  const inputChanged =
    result != null && lastRunSignature != null && formSignature(form) !== lastRunSignature;

  return (
    <Panel data-testid="dose-panel">
      <PanelTitle>처방 판정 테스트</PanelTitle>

      {presets.length > 0 && (
        <Section>
          <PresetNote>생성 {PRESET_GENERATED_AT} 기준 · 이름의 갈래는 생성 시점 기대값</PresetNote>
          <PresetSelect
            value={presetId}
            disabled={loading}
            onChange={(e) => onPresetChange(e.target.value)}
            data-testid="dose-preset-select"
            aria-label="프리셋 선택"
          >
            <option value="">프리셋 선택…</option>
            {presets.map((p) => (
              <option key={p.id} value={p.id} title={p.name}>
                {p.name}
              </option>
            ))}
          </PresetSelect>
        </Section>
      )}

      <Section>
        <SectionTitle>환자</SectionTitle>
        <Row>
          <Field>
            체중(kg)
            <NumberInput
              type="text"
              inputMode="decimal"
              value={form.weight}
              disabled={loading}
              onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))}
              data-testid="dose-weight"
            />
          </Field>
          <Field>
            월령(개월)
            <NumberInput
              type="text"
              inputMode="decimal"
              value={form.ageMonths}
              disabled={loading}
              onChange={(e) => setForm((f) => setAgeMonths(f, e.target.value))}
              data-testid="dose-age-months"
            />
          </Field>
          <Field>
            나이(년, 보조)
            <NumberInput
              type="text"
              inputMode="decimal"
              value={form.ageYears}
              disabled={loading}
              onChange={(e) => setForm((f) => setAgeYears(f, e.target.value))}
              data-testid="dose-age-years"
            />
          </Field>
        </Row>
      </Section>

      <Section>
        <SectionTitle>약품</SectionTitle>
        <Table>
          <HeaderRow>
            {ROW_COLUMN_LABELS.map((c) => (
              <div key={c}>{c}</div>
            ))}
          </HeaderRow>
          {form.rows.map((row) => (
            <div key={row.id}>
              <DrugRow>
                <div>
                  <DrugSearchInput
                    value={row.medicationName}
                    onChange={(v) => updateRow(row.id, (r) => rowWithName(r, v))}
                    onSelect={(drug) => updateRow(row.id, (r) => rowFromDrug(r, drug))}
                    itemCode={row.itemCode}
                    placeholder="약품명 또는 보험코드"
                    autoSearchOnNineDigits
                    disabled={loading}
                  />
                </div>
                <CellInput
                  type="text"
                  inputMode="decimal"
                  value={row.dose}
                  disabled={loading}
                  onChange={(e) => updateRow(row.id, (r) => ({ ...r, dose: e.target.value }))}
                  data-testid="dose-row-dose"
                  aria-label="1회 투약량"
                />
                <CellInput
                  type="text"
                  inputMode="numeric"
                  value={row.freq}
                  disabled={loading}
                  onChange={(e) => updateRow(row.id, (r) => ({ ...r, freq: e.target.value }))}
                  data-testid="dose-row-freq"
                  aria-label="1일 횟수"
                />
                <Button
                  buttonType="button"
                  type="Text"
                  size="Small"
                  variant="Ghost"
                  color="Negative"
                  disabled={loading}
                  onClick={() => removeRow(row.id)}
                  data-testid="dose-row-remove"
                  title="행 삭제"
                >
                  삭제
                </Button>
              </DrugRow>
              {(row.standardCode || row.insuranceCode) && (
                <CodeText>
                  표준 {row.standardCode || "—"} · 보험 {row.insuranceCode || "—"}
                </CodeText>
              )}
              {rowNeedsCodeWarning(row) && (
                <WarnText data-testid="dose-code-warning">
                  검색 목록에서 골라야 룰이 매칭됩니다
                </WarnText>
              )}
            </div>
          ))}
        </Table>
        <Row>
          <Button
            buttonType="button"
            type="Text"
            size="Small"
            variant="Outlined"
            color="Secondary"
            disabled={loading}
            onClick={addRow}
            data-testid="dose-add-row"
          >
            약품 추가
          </Button>
        </Row>
      </Section>

      <Row>
        <Button
          buttonType="button"
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          disabled={!runnable || loading}
          disabledColor
          onClick={onRun}
          data-testid="dose-run"
        >
          판정 실행
        </Button>
      </Row>

      <ResultSection>
        <ResultHeader>
          <SectionTitle>결과</SectionTitle>
          <Button
            buttonType="button"
            type="Text"
            size="Small"
            variant="Outlined"
            color="Secondary"
            disabled={!result || loading}
            disabledColor
            onClick={() => setShowRaw((v) => !v)}
            data-testid="dose-raw-toggle"
            title="요청·응답 원문"
          >
            {showRaw ? "결과 카드" : "</>"}
          </Button>
        </ResultHeader>

        {inputChanged && (
          <ChangedBanner data-testid="dose-changed-banner">
            입력이 바뀌었습니다 — 다시 실행하세요
          </ChangedBanner>
        )}

        {showRaw && result ? (
          // 원문 뷰가 열려 있으면 실행 중에도 직전 짝을 그대로 보여주다가, 응답 도착 시
          // result가 새 짝으로 갱신되며 함께 바뀐다(설계 결정 8, AC-58b).
          <RawView
            blocks={[
              {
                key: "request",
                title: "요청",
                value: result.request,
                testId: "dose-raw-request",
                copyTestId: "dose-raw-copy-request",
              },
              {
                key: "response",
                title: result.status !== null ? `응답 (HTTP ${result.status})` : "응답",
                value: result.body,
                emptyText: result.status === null ? "응답 없음" : undefined,
                testId: "dose-raw-response",
                copyTestId: "dose-raw-copy-response",
              },
            ]}
          />
        ) : loading ? (
          <Row>
            <Spinner />
            <span>판정 중…</span>
          </Row>
        ) : !result ? (
          <EmptyText>처방을 입력하고 판정을 실행하세요</EmptyText>
        ) : result.error ? (
          <ErrorCard result={result} />
        ) : result.data ? (
          <ResultView data={result.data} request={result.request} onOpenRule={onOpenRule} />
        ) : (
          <EmptyText>결과 없음</EmptyText>
        )}
      </ResultSection>
    </Panel>
  );
};

export default DoseReviewTestPanel;
